import { evaluateFormula, parseFormula, roundTo } from './formula.js';
export const ASSAY_ANALYTES_VERSION = 1;
export const ANALYTE_KINDS = ['result', 'input', 'derived', 'qc', 'meta', 'constant'];
const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const nonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
function validateAnalyte(raw, index, seenKeys) {
    const at = `analytes[${index}]`;
    if (!isPlainObject(raw))
        return `${at}: must be an object`;
    if (!nonEmptyString(raw.key))
        return `${at}.key: required non-empty string`;
    const key = raw.key;
    if (key !== key.trim())
        return `${at}.key: "${key}" must not have leading/trailing whitespace`;
    if (seenKeys.has(key))
        return `${at}.key: duplicate key "${key}"`;
    seenKeys.add(key);
    if (!nonEmptyString(raw.name))
        return `${at}.name: required non-empty string`;
    if (!ANALYTE_KINDS.includes(raw.kind)) {
        return `${at}.kind: must be one of ${ANALYTE_KINDS.join(', ')} (got ${JSON.stringify(raw.kind)})`;
    }
    const out = { key, name: raw.name.trim(), kind: raw.kind };
    if (raw.unit !== undefined) {
        if (raw.unit !== null && typeof raw.unit !== 'string')
            return `${at}.unit: must be a string or null`;
        out.unit = raw.unit === null ? null : raw.unit.trim();
    }
    if (raw.decimals !== undefined) {
        if (!Number.isInteger(raw.decimals) || raw.decimals < 0 || raw.decimals > 12) {
            return `${at}.decimals: must be an integer 0–12`;
        }
        out.decimals = raw.decimals;
    }
    if (raw.label !== undefined) {
        if (!nonEmptyString(raw.label))
            return `${at}.label: must be a non-empty string when present`;
        out.label = raw.label.trim();
    }
    if (raw.description !== undefined) {
        if (typeof raw.description !== 'string')
            return `${at}.description: must be a string`;
        out.description = raw.description;
    }
    if (raw.display !== undefined) {
        if (typeof raw.display !== 'boolean')
            return `${at}.display: must be boolean`;
        out.display = raw.display;
    }
    if (raw.inputs !== undefined) {
        if (out.kind !== 'derived')
            return `${at}.inputs: only allowed on kind 'derived' (got '${out.kind}')`;
        if (!Array.isArray(raw.inputs) || raw.inputs.length === 0)
            return `${at}.inputs: must be a non-empty array of keys`;
        const seen = new Set();
        for (const k of raw.inputs) {
            if (!nonEmptyString(k))
                return `${at}.inputs: every entry must be a non-empty string`;
            if (k === key)
                return `${at}.inputs: "${key}" cannot be its own input`;
            if (seen.has(k))
                return `${at}.inputs: duplicate "${k}"`;
            seen.add(k);
        }
        out.inputs = [...seen];
    }
    if (raw.headline !== undefined) {
        if (typeof raw.headline !== 'boolean')
            return `${at}.headline: must be boolean`;
        out.headline = raw.headline;
    }
    if (raw.formula !== undefined) {
        if (out.kind !== 'derived')
            return `${at}.formula: only allowed on kind 'derived' (got '${out.kind}')`;
        if (typeof raw.formula !== 'string')
            return `${at}.formula: must be a string`;
        const parsed = parseFormula(raw.formula);
        if (!parsed.ok)
            return `${at}.${parsed.error}`;
        out.formula = raw.formula.trim();
    }
    if (raw.optional !== undefined) {
        if (out.kind !== 'derived')
            return `${at}.optional: only allowed on kind 'derived' (got '${out.kind}')`;
        if (typeof raw.optional !== 'boolean')
            return `${at}.optional: must be boolean`;
        out.optional = raw.optional;
    }
    if (out.kind === 'constant') {
        if (typeof raw.value !== 'number' || !Number.isFinite(raw.value))
            return `${at}.value: a constant needs a finite number value`;
        out.value = raw.value;
        for (const forbidden of ['headline', 'display', 'inputs', 'default', 'source']) {
            if (raw[forbidden] !== undefined)
                return `${at}.${forbidden}: not allowed on kind 'constant'`;
        }
    }
    else if (raw.value !== undefined) {
        return `${at}.value: only allowed on kind 'constant' (got '${out.kind}')`;
    }
    if (raw.default !== undefined) {
        if (out.kind !== 'input' && out.kind !== 'result')
            return `${at}.default: only allowed on kind 'input' or 'result' (got '${out.kind}')`;
        if (typeof raw.default !== 'number' || !Number.isFinite(raw.default))
            return `${at}.default: must be a finite number`;
        out.default = raw.default;
    }
    if (raw.source !== undefined) {
        if (out.kind !== 'input')
            return `${at}.source: only allowed on kind 'input' (got '${out.kind}')`;
        if (!isPlainObject(raw.source))
            return `${at}.source: must be an object { assay, key }`;
        if (!nonEmptyString(raw.source.assay))
            return `${at}.source.assay: required non-empty string`;
        if (!nonEmptyString(raw.source.key))
            return `${at}.source.key: required non-empty string`;
        out.source = { assay: raw.source.assay.trim(), key: raw.source.key.trim() };
        if (raw.default !== undefined)
            return `${at}.default: not allowed on a sourced input (the cross-test job fills it)`;
    }
    return out;
}
const MAX_KEY_PATTERN_LENGTH = 200;
const MAX_DYNAMIC_KEY_LENGTH = 128;
/**
 * Rejects the constructs that make a backtracking regex engine exponential on
 * a crafted key: a repeated group containing a quantifier or an alternation
 * (`(a+)+`, `(a|a)*`) and backreferences.
 */
function unsafePatternReason(pattern) {
    var _a;
    const groups = [{ quantified: false, alternation: false }];
    const quantifierAt = (i) => {
        const c = pattern[i];
        return c === '*' || c === '+' || c === '?' || (c === '{' && /^\{\d+(,\d*)?\}/.test(pattern.slice(i)));
    };
    for (let i = 0; i < pattern.length; i++) {
        const c = pattern[i];
        const top = groups[groups.length - 1];
        if (c === '\\') {
            if (/[1-9k]/.test(pattern[i + 1] ?? ''))
                return 'backreferences are not allowed';
            i++;
        }
        else if (c === '[') {
            for (i++; i < pattern.length && pattern[i] !== ']'; i++)
                if (pattern[i] === '\\')
                    i++;
        }
        else if (c === '(') {
            groups.push({ quantified: false, alternation: false });
            if (pattern[i + 1] === '?')
                i += pattern[i + 2] === '<' && pattern[i + 3] !== '=' && pattern[i + 3] !== '!' ? 2 : 1;
        }
        else if (c === ')') {
            const inner = groups.pop();
            const repeated = quantifierAt(i + 1) && pattern[i + 1] !== '?';
            if (repeated && (inner.quantified || inner.alternation)) {
                return 'a repeated group must not contain a quantifier or alternation';
            }
            (_a = groups[groups.length - 1]).quantified || (_a.quantified = inner.quantified);
        }
        else if (c === '|') {
            top.alternation = true;
        }
        else if (quantifierAt(i)) {
            top.quantified = true;
        }
    }
    return null;
}
function validateDynamic(raw) {
    if (!isPlainObject(raw))
        return 'dynamic: must be an object';
    if (!nonEmptyString(raw.keyPattern))
        return 'dynamic.keyPattern: required non-empty string';
    if (raw.keyPattern.length > MAX_KEY_PATTERN_LENGTH) {
        return `dynamic.keyPattern: must be at most ${MAX_KEY_PATTERN_LENGTH} characters`;
    }
    try {
        new RegExp(raw.keyPattern);
    }
    catch (e) {
        return `dynamic.keyPattern: invalid regular expression (${e.message})`;
    }
    const unsafe = unsafePatternReason(raw.keyPattern);
    if (unsafe)
        return `dynamic.keyPattern: ${unsafe}`;
    if (raw.nameFrom !== 'label' && raw.nameFrom !== 'key')
        return "dynamic.nameFrom: must be 'label' or 'key'";
    if (!ANALYTE_KINDS.includes(raw.kind) || raw.kind === 'constant') {
        const allowed = ANALYTE_KINDS.filter(k => k !== 'constant');
        return raw.kind === 'constant'
            ? 'dynamic.kind: a constant must be declared as an analyte with a value, not matched by a pattern'
            : `dynamic.kind: must be one of ${allowed.join(', ')}`;
    }
    const out = { keyPattern: raw.keyPattern, nameFrom: raw.nameFrom, kind: raw.kind };
    if (raw.unitFrom !== undefined) {
        if (raw.unitFrom !== 'entry')
            return "dynamic.unitFrom: must be 'entry' when present";
        out.unitFrom = 'entry';
    }
    return out;
}
/**
 * Validate and normalise an `assay.analytes` value. Returns the normalised
 * definition (trimmed strings, unknown properties dropped) or a single error
 * naming the first problem found.
 */
export function validateAssayAnalytes(value) {
    if (!isPlainObject(value))
        return { error: 'analytes: must be an object { version, analytes: [...] }' };
    if (value.version !== ASSAY_ANALYTES_VERSION) {
        return { error: `analytes.version: must be ${ASSAY_ANALYTES_VERSION} (got ${JSON.stringify(value.version)})` };
    }
    if (!Array.isArray(value.analytes))
        return { error: 'analytes.analytes: must be an array' };
    const out = { version: ASSAY_ANALYTES_VERSION, analytes: [] };
    if (value.strict !== undefined) {
        if (typeof value.strict !== 'boolean')
            return { error: 'analytes.strict: must be boolean' };
        out.strict = value.strict;
    }
    const seenKeys = new Set();
    for (let i = 0; i < value.analytes.length; i++) {
        const result = validateAnalyte(value.analytes[i], i, seenKeys);
        if (typeof result === 'string')
            return { error: `analytes.${result}` };
        out.analytes.push(result);
    }
    if (value.dynamic !== undefined) {
        const result = validateDynamic(value.dynamic);
        if (typeof result === 'string')
            return { error: `analytes.${result}` };
        out.dynamic = result;
    }
    const constantKeys = new Set(out.analytes.filter(a => a.kind === 'constant').map(a => a.key));
    for (const a of out.analytes) {
        for (const k of a.inputs ?? []) {
            if (!seenKeys.has(k))
                return { error: `analytes: "${a.key}".inputs references undeclared key "${k}"` };
            if (constantKeys.has(k))
                return { error: `analytes: "${a.key}".inputs lists constant "${k}"; a formula reads constants by key without listing them` };
        }
        if (a.formula !== undefined) {
            if (!a.inputs)
                return { error: `analytes: "${a.key}".formula needs inputs to read from` };
            const parsed = parseFormula(a.formula);
            if (!parsed.ok)
                return { error: `analytes: "${a.key}".${parsed.error}` };
            for (const name of parsed.formula.identifiers) {
                if (!a.inputs.includes(name) && !constantKeys.has(name)) {
                    return { error: `analytes: "${a.key}".formula reads "${name}", which is neither one of its inputs nor a constant` };
                }
            }
        }
    }
    if (out.analytes.length === 0 && !out.dynamic) {
        return { error: 'analytes: declare at least one analyte or a dynamic key pattern' };
    }
    return { value: out };
}
/** Declared analyte for a worksheet key, or undefined. Does not consider `dynamic`. */
export function analyteForKey(def, key) {
    return def.analytes.find(a => a.key === key);
}
function dynamicKeyMatcher(def) {
    if (!def.dynamic)
        return () => false;
    const pattern = new RegExp(def.dynamic.keyPattern);
    return key => key.length <= MAX_DYNAMIC_KEY_LENGTH && pattern.test(key);
}
export function isDeclaredKey(def, key) {
    return analyteForKey(def, key) !== undefined || dynamicKeyMatcher(def)(key);
}
/** True when worksheet_data carries a non-empty `{ value }` entry for the key. */
export const hasValue = (worksheetData, key) => {
    if (!isPlainObject(worksheetData))
        return false;
    const e = worksheetData[key];
    return isPlainObject(e) && e.value !== null && e.value !== undefined && e.value !== '';
};
/**
 * The analyte that headlines a test of this assay, or null when the assay
 * declares none. Headlines are tried in declaration order: given the test's
 * worksheet_data, the first one with a value wins (titrator: carbonate, else
 * bicarbonate); without it, the first declared wins.
 */
export function headlineAnalyte(def, worksheetData) {
    const present = (key) => worksheetData === undefined || (isPlainObject(worksheetData) && hasValue(worksheetData, key));
    for (const a of def.analytes) {
        if (a.headline === true && present(a.key))
            return a;
    }
    return null;
}
/** The definition's constants, by key. */
export function constantsOf(def) {
    const out = {};
    for (const a of def.analytes)
        if (a.kind === 'constant' && typeof a.value === 'number')
            out[a.key] = a.value;
    return out;
}
const parsedFormulas = new Map();
function parsedFormulaOf(a) {
    if (!a.formula)
        return null;
    const cached = parsedFormulas.get(a.formula);
    if (cached !== undefined)
        return cached;
    const parsed = parseFormula(a.formula);
    const value = parsed.ok ? parsed.formula : null;
    parsedFormulas.set(a.formula, value);
    return value;
}
/** The constant keys a derived analyte's formula reads, in first-use order. */
export function formulaConstants(def, a) {
    const parsed = parsedFormulaOf(a);
    if (!parsed)
        return [];
    const constants = constantsOf(def);
    return parsed.identifiers.filter(name => name in constants);
}
/**
 * Compute a derived analyte from its formula: `getInput` supplies the
 * current value of each input key (null when absent), constants come from
 * the definition, and the result is rounded to the entry's `decimals`. Null
 * when the entry has no formula, an input is missing, or the arithmetic has
 * no finite result (division by zero).
 */
export function evaluateDerived(def, a, getInput) {
    const parsed = parsedFormulaOf(a);
    if (!parsed)
        return null;
    const constants = constantsOf(def);
    const value = evaluateFormula(parsed, name => (name in constants ? constants[name] : getInput(name)));
    if (value === null)
        return null;
    return a.decimals === undefined ? value : roundTo(value, a.decimals);
}
/**
 * The analytes a result page should show, in declaration order: the ones
 * flagged `display` when the definition flags any, else every `result` and
 * `derived` analyte. Inputs, qc, meta and constants are never shown unless flagged.
 */
export function displayAnalytes(def) {
    const flagged = def.analytes.filter(a => a.display === true);
    if (flagged.length > 0)
        return flagged;
    return def.analytes.filter(a => a.kind === 'result' || a.kind === 'derived');
}
export function analyteLabel(a) {
    if (a.label)
        return a.label;
    return a.unit ? `${a.name} (${a.unit})` : a.name;
}
const isPlaceholder = (entry) => entry === null || entry === undefined
    || (isPlainObject(entry) && 'value' in entry && (entry.value === null || entry.value === undefined || entry.value === ''));
const normaliseUnit = (u) => typeof u === 'string' && u.trim().length > 0 ? u.trim().toLowerCase() : null;
/**
 * Compare a worksheet_data value against an assay's analytes definition.
 * Pure; the caller decides what to do with the result (warn, reject, log).
 *
 * `worksheetData` that is not a plain object yields no issues here — the
 * `worksheet_data` attribute's own shape is the attribute layer's concern.
 */
export function checkWorksheetData(def, worksheetData) {
    const check = { issues: [], undeclaredKeys: [], missingResultKeys: [] };
    if (!isPlainObject(worksheetData))
        return check;
    const matchesDynamic = dynamicKeyMatcher(def);
    for (const [key, entry] of Object.entries(worksheetData)) {
        const declared = analyteForKey(def, key);
        if (!declared && !matchesDynamic(key)) {
            // Parsers write null placeholders for every column of a shared file (Shimadzu: TC/TN/TOC/POC on an NPOC test).
            if (isPlaceholder(entry))
                continue;
            check.undeclaredKeys.push(key);
            check.issues.push({ code: 'undeclared_key', key, message: `"${key}" is not declared by the assay's analytes` });
            continue;
        }
        if (entry === null || entry === undefined)
            continue;
        if (!isPlainObject(entry) || !('value' in entry)) {
            check.issues.push({ code: 'bad_entry', key, message: `"${key}" must be an object with a value property` });
            continue;
        }
        const declaredUnit = normaliseUnit(declared?.unit);
        const entryUnit = normaliseUnit(entry.unit);
        if (declaredUnit && entryUnit && declaredUnit !== entryUnit) {
            check.issues.push({
                code: 'unit_mismatch',
                key,
                message: `"${key}" written with unit "${entry.unit}" but the assay declares "${declared.unit}"`,
            });
        }
    }
    for (const a of def.analytes) {
        if (a.kind === 'result' && !(a.key in worksheetData))
            check.missingResultKeys.push(a.key);
        if (a.kind === 'derived' && a.inputs && hasValue(worksheetData, a.key)) {
            const missing = a.inputs.filter(k => !hasValue(worksheetData, k));
            if (missing.length > 0) {
                check.issues.push({
                    code: 'missing_inputs',
                    key: a.key,
                    message: `"${a.key}" is derived from ${a.inputs.join(', ')} but ${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} missing`,
                });
            }
        }
    }
    return check;
}
